
import { computed, defineComponent, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import MixSelectors
  from '@/views/dashboard/_components/group-policy-settings/_components/mix-selectors/mix-selectors.vue'
import { ElMessage } from 'element-plus'
import { useRoute, useRouter } from 'vue-router'
import ContentAdaptLayout from '@/views/dashboard/_components/content-adapt-layout/content-adapt-layout.vue'
import TaskTimeEdit from '@/views/dashboard/_components/task-time/task-time-edit.vue'
import SoftwareSelect from '@/views/dashboard/_components/software-select/software-select.vue'
import PolicyBindScope from '@/views/dashboard/policy/_components/policy-bind-scope.vue'
import { editToList, PolicyCommonService } from '@/views/dashboard/policy/policy-common.service'
import { PermissionFe } from '@/services/service-permission.service'
import { PolicySettingType, PolicyType } from '@/views/dashboard/_enum/policy-type'
import {
  DEFAULT_SETTING,
  SettingsRes
} from '@/views/dashboard/data-safe/screen-watermark-edit/screen-watermark-edit.service'
import { WatermarkType } from '@/views/dashboard/data-safe/screen-watermark/_enum/watermark-type'
import { WatermarkMode } from '@/views/dashboard/data-safe/screen-watermark/_enum/watermark-mode'
import {
  getWatermarkDensityOptions,
  getWatermarkFieldOptions,
} from '@/views/dashboard/data-safe/screen-watermark/_enum/screen-watermark-labels'
import ProcessEdit from '@/views/dashboard/_components/process-edit/process-edit.vue'
import WatermarkPreview
  from '@/views/dashboard/data-safe/screen-watermark-edit/_components/watermark-preview/watermark-preview.vue'
import {
  WatermarkField,
  WatermarkFieldSlot
} from '@/views/dashboard/data-safe/screen-watermark/_enum/watermark-field'
import { isObjectFieldEmpty } from '@/utils/tool'
import { ProcessInfos } from '@/views/dashboard/data-safe/screen-watermark/screen-watermark.service'

export default defineComponent({
  components: {
    WatermarkPreview,
    ProcessEdit,
    PolicyBindScope,
    SoftwareSelect,
    TaskTimeEdit,
    MixSelectors,
    ContentAdaptLayout
  },
  props: {
    /** 抽屉模式：由列表页在侧边抽屉中打开，取消/保存时 emit 而非路由跳转 */
    drawerMode: { type: Boolean, default: false },
    initialPolicyId: { type: String, default: '' },
    initialFromGroupId: { type: String, default: undefined },
  },
  emits: ['close', 'success'],
  setup (props, context) {
    const { t, locale } = useI18n()
    const watermarkFieldOptions = computed(() => {
      void locale.value
      return getWatermarkFieldOptions(t)
    })
    const watermarkDensityOptions = computed(() => {
      void locale.value
      return getWatermarkDensityOptions(t)
    })
    const injector = useReflectiveInjector([PolicyCommonService])
    const policyCommonService = injector.get(PolicyCommonService)

    const route = useRoute()
    const router = useRouter()
    const isLoading = ref<boolean>(false)
    const isProcessShow = ref<boolean>(false)
    const policyId = ref(props.drawerMode ? (props.initialPolicyId || '') : (route.query.policy_id as string))
    const isReadonly = ref(props.drawerMode ? false : !!route.query.readonly)
    const strategyData = ref({
      strategy: JSON.parse(JSON.stringify(DEFAULT_SETTING)),
      bind_mode: 0,
      device_ids: [] as string[],
      strategy_group_ids: [] as string[],
    } as SettingsRes)
    const formNode = ref()
    const fromGroupId = props.drawerMode ? (props.initialFromGroupId || undefined) : (route.query.from_group_id as string || undefined)
    const formRules = reactive({
      name: [
        {
          required: true,
          message: () => t('screenWatermark.pleaseEnterPolicyName'),
          trigger: 'blur',
        },
      ],
      'strategy.software_process': [
        {
          required: true,
          validator: (rule: any, value: string[], callback: any) => {
            const processList = strategyData.value.strategy.process_infos.filter(v => !isObjectFieldEmpty(v))
            const isExistEmpty = processList.find((item: ProcessInfos) => !item.name)
            if (!value.length && !processList.length) {
              callback(new Error(t('screenWatermark.pleaseAddTarget')))
            } else if (isExistEmpty) {
              callback(new Error(t('screenWatermark.processNameRequired')))
            } else {
              callback()
            }
          },
          trigger: 'change',
        },
      ],
      strategy_group_ids: [
        {
          validator: (rule: any, value: any, callback: (e?: Error) => void) => {
            const mode = strategyData.value.bind_mode ?? 0
            if (mode === 0 && (!value || !value.length)) {
              callback(new Error(t('screenWatermark.pleaseSelectGroup')))
            } else {
              callback()
            }
          },
          trigger: 'change',
        },
      ],
      device_ids: [
        {
          validator: (rule: any, value: any, callback: (e?: Error) => void) => {
            const mode = strategyData.value.bind_mode ?? 0
            if (mode === 1 && (!value || !value.length)) {
              callback(new Error(t('policyBindScope.needTerminals')))
            } else {
              callback()
            }
          },
          trigger: 'change',
        },
      ],
      'strategy.wmdata.rotate_angle': [
        {
          required: true,
          message: () => t('screenWatermark.pleaseSetTilt'),
          trigger: 'blur',
        },
      ],
      'strategy.wmdata.fields': [
        {
          required: true,
          message: () => t('screenWatermark.pleaseSelectDisplay'),
          trigger: 'blur',
        },
      ],
    })

    const wmdata = computed(() => strategyData.value.strategy.wmdata)

    function getSettings () {
      isLoading.value = true
      policyCommonService.getSettings({
        id: policyId.value,
        type: PolicySettingType.screenWatermark,
      }).then(response => {
        strategyData.value = response
        if (!strategyData.value.strategy.process_infos.length) {
          strategyData.value.strategy.process_infos = [{}]
        }
        initProcessShow()
      }).finally(() => {
        isLoading.value = false
      })
    }

    async function saveSetting () {
      const strategy = {
        ...strategyData.value.strategy,
        process_infos: strategyData.value.strategy.process_infos.filter(v => !isObjectFieldEmpty(v))
      }

      if (!await checkBeforeSubmit()) {
        return
      }
      policyCommonService.saveSettings({
        id: policyId.value,
        strategy: JSON.stringify(strategy),
        name: strategyData.value.name,
        group_ids: strategyData.value.strategy_group_ids ?? [],
        device_ids: strategyData.value.device_ids,
        bind_mode: strategyData.value.bind_mode ?? 0,
        type: PolicySettingType.screenWatermark,
        strategy_type: PolicyType.screenWatermark,
      }).then(() => {
        ElMessage.success(t('screenWatermark.saveSuccess'))
        if (props.drawerMode) {
          context.emit('success')
        } else {
          goListPage()
        }
      })
    }

    async function checkBeforeSubmit () {
      if (!await formNode.value.validate().catch(() => false)) {
        ElMessage.error(t('screenWatermark.formError'))
        return false
      }
      return true
    }

    function cancel () {
      if (props.drawerMode) {
        context.emit('close')
      } else {
        goListPage()
      }
    }

    function handleUpdateSoftwareSelect (e) {
      strategyData.value.strategy.software_process = e
      formNode.value?.validateField('strategy.software_process')
    }

    function toggleProcessShow () {
      isProcessShow.value = !isProcessShow.value
    }

    function initProcessShow () {
      isProcessShow.value = !!strategyData.value.strategy?.process_infos?.find(v => !isObjectFieldEmpty(v))
    }

    function changeField (e, field) {
      if (e) {
        strategyData.value.strategy.wmdata.fields.push(field)
      } else {
        const index = wmdata.value.fields.indexOf(field)
        strategyData.value.strategy.wmdata.fields.splice(index, 1)
      }
      strategyData.value.strategy.wmdata.fields = wmdata.value.fields.sort((a:string, b:string) => WatermarkFieldSlot[a] - WatermarkFieldSlot[b])
      formNode.value?.validateField('strategy.wmdata.fields')
    }

    function changeWatermarkType (value) {
      if (value === WatermarkType.hidden) {
        strategyData.value.strategy.wmdata.opacity = 1
      } else if (value === WatermarkType.visible) {
        strategyData.value.strategy.wmdata.opacity = 15
      } else if (value === WatermarkType.custom) {
        strategyData.value.strategy.wmdata.opacity = 5
      }
    }

    function goListPage () {
      editToList({ path: '/dashboard/screen-watermark', fromGroupId, })
    }

    function initPage () {
      if (policyId.value) {
        getSettings()
      }
    }

    if (props.drawerMode && props.initialPolicyId) {
      policyId.value = props.initialPolicyId
    }
    initPage()

    return {
      WatermarkField,
      watermarkFieldOptions,
      watermarkDensityOptions,
      PermissionFe,
      WatermarkType,
      WatermarkMode,
      groupEditStrategyOver: { type: PolicyType.screenWatermark, current: policyId.value },
      isLoading,
      fromGroupId,
      isProcessShow,
      policyId,
      isReadonly,
      strategyData,
      formNode,
      formRules,
      wmdata,
      handleUpdateSoftwareSelect,
      toggleProcessShow,
      changeField,
      changeWatermarkType,
      saveSetting,
      cancel,
    }
  }
})
